import { getAllRouteStatisticsOptions, getAllActiveRoutesOptions } from '@/api/main'
import { RouteStatistics } from '@/api/main/types.gen'
import PageShell from '@/components/Common/PageShell'
import { useTable } from '@/components/Table/useTable'

import { requirePermission } from '@/utils/auth'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { round } from 'lodash-es'
import { useMemo } from 'react'
import { ActionIcon, Group, Tooltip } from '@mantine/core'
import { TbMap2 } from 'react-icons/tb'
import { GenericTable, TableColumn } from '@/components/Table/GenericTable'
import { createRouteNameMap } from '@/utils/routes'
import ErrorState from '@/components/Common/ErrorState'
import { QueryBoundary } from '@/components/Common/QueryBoundary'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { useAuthStore } from '@/stores/auth'
import { hasAnyPermission } from '@/utils/permissions'

type EnhancedRouteStatistics = RouteStatistics & {
  name?: string
  planning_area_id?: string
  plan?: string
}

export const Route = createFileRoute('/routes')({
  beforeLoad: () => requirePermission('routes'),
  component: () => (
    <QueryBoundary
      loadingFallback={<RoutesLoadingFallback />}
      errorTitle={i18next.t('failedToLoadRoutes', { ns: 'routeManagement' })}
      errorMessage={i18next.t('unableToLoadRoutes', { ns: 'routeManagement' })}
      pageTitle={i18next.t('routes', { ns: 'routeManagement' })}
      backButtonHref="/routes"
    >
      <RoutesPage />
    </QueryBoundary>
  ),
  errorComponent: ({ error }) => (
    <PageShell title={i18next.t('errorLoadingRoutes', { ns: 'routeManagement' })}>
      <ErrorState
        title={i18next.t('failedToLoadRoutes', { ns: 'routeManagement' })}
        message={error.message || i18next.t('unableToLoadRoutes', { ns: 'routeManagement' })}
      />
    </PageShell>
  ),
})

function RoutesLoadingFallback() {
  const { t } = useTranslation('routeManagement')

  const defaultColumns: TableColumn<EnhancedRouteStatistics>[] = [
    { key: 'name', label: t('routeName'), visible: true },
    { key: 'route_id', label: t('routeId'), visible: true },
    { key: 'route_version', label: t('routeVersion'), visible: true },
    { key: 'total_km', label: t('routeLength'), visible: true, align: 'right' },
  ]

  return (
    <PageShell title={t('routes')}>
      <GenericTable
        stickyHeader
        data={[]}
        isLoading={true}
        error={null}
        currentPage={1}
        onPageChange={() => {}}
        onFilterFieldChange={() => {}}
        onSearchChange={() => {}}
        getRowKey={() => ''}
        emptyMessage={t('noRoutesFound')}
        paginatedData={[]}
        filteredData={[]}
        itemsPerPage={Infinity}
        search=""
        columns={defaultColumns}
        onColumnsChange={() => {}}
        filterField={undefined}
        isDebouncing={false}
        hideCsvDownload={false}
        csvFileName="routes.csv"
        sortDirection="asc"
        onSortDirectionChange={() => {}}
        sortField="route_id"
        onSortFieldChange={() => {}}
      />
    </PageShell>
  )
}

function RoutesPage() {
  const { t } = useTranslation('routeManagement')
  const { data: routeStatistics } = useSuspenseQuery(getAllRouteStatisticsOptions())
  const { data: allRoutes } = useSuspenseQuery(getAllActiveRoutesOptions())
  const userPermissions = useAuthStore(state => state.userPermissions)
  const canPlan = hasAnyPermission(userPermissions, 'maps') && hasAnyPermission(userPermissions, 'routes')

  const routeNameMap = useMemo(() => createRouteNameMap(allRoutes), [allRoutes])
  const planningAreaByRoute = useMemo(() => {
    const map = new Map<string, string>()
    for (const route of allRoutes) {
      map.set(`${route.id}-${route.version}`, route.planning_area_id)
    }
    return map
  }, [allRoutes])

  const enhancedData = useMemo(
    () =>
      routeStatistics?.map(item => {
        const key = `${item.route_id}-${item.route_version}`
        const name = routeNameMap.get(key)
        return {
          ...item,
          total_km: round(item.total_km, 2),
          name,
          planning_area_id: planningAreaByRoute.get(key),
        }
      }) || [],
    [routeStatistics, routeNameMap, planningAreaByRoute]
  )

  const table = useTable<EnhancedRouteStatistics>({
    data: enhancedData,
    itemsPerPage: Infinity,
    defaultSortField: 'route_id',
    defaultSortDirection: 'asc',
    tableKey: 'routesTableColumns',
    columns: [
      { key: 'name', label: t('routeName'), visible: true },
      { key: 'route_id', label: t('routeId'), visible: true },
      { key: 'route_version', label: t('routeVersion'), visible: true },
      { key: 'total_km', label: t('routeLength'), visible: true, align: 'right' },
      ...(canPlan ? [{ key: 'plan' as const, label: t('plan'), visible: true }] : []),
    ] as TableColumn<EnhancedRouteStatistics>[],
    searchDebounceMs: 300,
  })

  const columnsWithRenderers = table.tableColumns.map(col => {
    if (col.key === 'route_id')
      return {
        ...col,
        render: (item: EnhancedRouteStatistics) => (
          <Link
            to="/route/$routeId/$version"
            params={{ routeId: item.route_id.toString(), version: item.route_version.toString() }}
            style={{
              color: 'var(--mantine-color-blue-6)',
              textDecoration: 'underline',
            }}
          >
            {item.route_id}
          </Link>
        ),
      }

    if (col.key === 'total_km') {
      return {
        ...col,
        render: (item: EnhancedRouteStatistics) => round(item.total_km, 1),
      }
    }

    if (col.key === 'plan') {
      return {
        ...col,
        render: (item: EnhancedRouteStatistics) =>
          item.planning_area_id ? (
            <Group gap={4}>
              <Tooltip label={t('plan')} withArrow>
                <Link
                  to="/route-planner/$areaId/$routeId/$version"
                  params={{
                    areaId: item.planning_area_id,
                    routeId: item.route_id,
                    version: item.route_version,
                  }}
                >
                  <ActionIcon variant="default" size={32} aria-label={t('plan')}>
                    <TbMap2 size={18} />
                  </ActionIcon>
                </Link>
              </Tooltip>
            </Group>
          ) : null,
      }
    }

    return col
  })

  return (
    <PageShell title={t('routes')}>
      <GenericTable
        {...table.tableProps}
        stickyHeader
        isLoading={false}
        error={null}
        getRowKey={item => item.route_id}
        emptyMessage={t('noRoutesFound')}
        columns={columnsWithRenderers}
        hideCsvDownload={false}
        csvFileName="routes.csv"
      />
    </PageShell>
  )
}
